import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Card } from '@/components/ui/card'
import { ChevronRight, TowerControl } from '@/components/icons'
import { MenuItem } from './ui/menu-item'
import { SearchInput } from './ui/search-input'
import { hapticLight } from '@/lib/haptics'
import { TopBarBackIconButton } from './topbar'
import { AppLayout } from './AppLayout'
import { EmptyState } from './EmptyState'
import { ListSkeleton } from './ui/skeleton-presets'
import { DesktopPage } from './desktop/DesktopPage'
import { DesktopPageHeader } from './desktop/DesktopPageHeader'
import { useIsDesktop } from './ui/use-mobile'
import { AirportDetailDrawer } from './AirportDetailDrawer'
import { ensureAirportDb, listAirports, type Airport } from '@/lib/airport-db'
import { useAirportDirectorySearch } from '../lib/hooks'

const PAGE_SIZE = 200

interface AirportDirectoryProps {
  initialSearch?: string
}

export function AirportDirectory({ initialSearch }: AirportDirectoryProps) {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState(initialSearch ?? '')
  const [debouncedQuery, setDebouncedQuery] = useState(initialSearch ?? '')
  const [dbReady, setDbReady] = useState(false)
  const [airports, setAirports] = useState<Airport[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [drawerIcao, setDrawerIcao] = useState<string | null>(null)
  // Offset tracked in a ref so back-to-back observer callbacks never re-fetch
  // the same page before React commits the appended rows.
  const offsetRef = useRef(0)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const isSearching = debouncedQuery.trim().length >= 1
  const { results: searchResults, isLoading: searchLoading } = useAirportDirectorySearch(
    debouncedQuery.trim(),
    200
  )

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
      navigate({
        to: '/airport-directory',
        search: { search: searchQuery || undefined } as any,
        replace: true,
      })
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery, navigate])

  useEffect(() => {
    let cancelled = false
    ensureAirportDb()
      .then(() => {
        if (cancelled) return
        const first = listAirports(0, PAGE_SIZE)
        offsetRef.current = first.length
        setAirports(first)
        setHasMore(first.length === PAGE_SIZE)
        setDbReady(true)
      })
      .catch(() => {
        if (cancelled) return
        setHasMore(false)
        setDbReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const loadMore = useCallback(() => {
    if (!dbReady || !hasMore) return
    const next = listAirports(offsetRef.current, PAGE_SIZE)
    offsetRef.current += next.length
    if (next.length > 0) setAirports((prev) => [...prev, ...next])
    setHasMore(next.length === PAGE_SIZE)
  }, [dbReady, hasMore])

  useEffect(() => {
    if (isSearching || !dbReady || !hasMore) return
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore()
      },
      { rootMargin: '800px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [isSearching, dbReady, hasMore, loadMore])

  // Browse and search render the same letter-grouped layout. Browse pages
  // arrive ICAO-sorted; search results arrive rank-sorted (ICAO-first tiers
  // decide which rows survive the limit), so both get normalized to
  // alphabetical letters with ICAO-sorted rows, '#' bucket last.
  const groups = useMemo(() => {
    const source = isSearching ? searchResults : airports
    const map = new Map<string, Airport[]>()
    for (const airport of source) {
      // ICAO prefixes encode geography, so grouping by code clusters nearby airports.
      const first = airport.icaoCode.charAt(0).toUpperCase()
      const letter = first >= 'A' && first <= 'Z' ? first : '#'
      const list = map.get(letter)
      if (list) list.push(airport)
      else map.set(letter, [airport])
    }
    const entries = [...map.entries()]
    entries.sort(([a], [b]) => (a === '#' ? 1 : b === '#' ? -1 : a.localeCompare(b)))
    if (isSearching) {
      for (const [, list] of entries) {
        list.sort((a, b) => a.icaoCode.localeCompare(b.icaoCode))
      }
    }
    return entries
  }, [airports, searchResults, isSearching])

  const handleOpen = (icao: string) => {
    hapticLight()
    setDrawerIcao(icao)
  }

  const renderRows = (list: Airport[]) => (
    <div className="flex flex-col gap-2">
      {list.map((airport, index) => (
        <Fragment key={airport.icaoCode}>
          {index > 0 && <hr className="border-border-L1" />}
          <MenuItem
            size="large"
            secondaryText={airport.name}
            rightIcon={<ChevronRight className="size-5 text-content-primary-subtle" />}
            onClick={() => handleOpen(airport.icaoCode)}
          >
            {airport.icaoCode}
          </MenuItem>
        </Fragment>
      ))}
    </div>
  )

  const listSkeleton = (
    <Card className="p-2">
      <ListSkeleton rows={8} />
    </Card>
  )

  const body = (
    <div className="flex flex-col gap-4 pt-4">
      {/* Sticky search band; letter headers pin right below it (top-11 = input height) */}
      <div className="sticky top-0 z-20 bg-surface-brand-L0">
        <SearchInput
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onClear={() => setSearchQuery('')}
        />
      </div>

      {!dbReady || (isSearching && searchLoading && searchResults.length === 0) ? (
        listSkeleton
      ) : isSearching && searchResults.length === 0 ? (
        <EmptyState
          icon={TowerControl}
          title="No airports found"
          description="Use up to 4 letters for an ICAO code, or 5+ for an airport name."
        />
      ) : (
        <>
          {groups.map(([letter, list]) => (
            <div key={letter} className="flex flex-col gap-2">
              <p className="sticky top-11 z-10 bg-surface-brand-L0 px-1 py-1 text-xs font-semibold uppercase leading-4 tracking-[1.5px] text-content-primary-subtle-mid">
                {letter}
              </p>
              <Card className="p-2">{renderRows(list)}</Card>
            </div>
          ))}
          {!isSearching && hasMore && (
            <>
              <div ref={sentinelRef} className="h-px" />
              <Card className="p-2">
                <ListSkeleton rows={3} />
              </Card>
            </>
          )}
        </>
      )}

      <AirportDetailDrawer
        icao={drawerIcao}
        isOpen={drawerIcao !== null}
        onClose={() => setDrawerIcao(null)}
      />
    </div>
  )

  const isDesktop = useIsDesktop()

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader title="Airport Directory" />
        {body}
      </DesktopPage>
    )
  }

  return (
    <AppLayout
      topBar={<TopBarBackIconButton title="Airport Directory" />}
      className="pb-4"
    >
      {body}
    </AppLayout>
  )
}
